import { Link } from "react-router-dom";
import { MapPin, GraduationCap, Heart, Users, ArrowRight } from "lucide-react";

const ABOUT_PHOTO = "https://customer-assets.emergentagent.com/job_144b4408-104d-4a3e-8d1d-a71d38c76165/artifacts/ok08oe3h_About%20Me%20Photo.jpg";
const EXTRA_PHOTO = "https://customer-assets.emergentagent.com/job_144b4408-104d-4a3e-8d1d-a71d38c76165/artifacts/gadcjwgo_Extra%20Picture%20if%20Needed.jpg";

const AboutPage = () => {
  const timeline = [
    { icon: MapPin, year: "Age 2", title: "Moved to Laughlin", description: "My family moved to Laughlin, settling into a small apartment. Mom worked as a food server at the Edgewater, Dad was a cook at the Golden Nugget while taking construction jobs." },
    { icon: MapPin, year: "Childhood", title: "Grew Up in Laughlin", description: "We didn't have much, but my parents never let that define us. They worked tirelessly, showing us the value of hard work, sacrifice, and doing whatever it takes for family." },
    { icon: GraduationCap, year: "Education", title: "Attended UNR in Reno", description: "Lived in Reno while attending the University of Nevada, Reno. Nevada has been home for 39 of my 41 years." },
    { icon: MapPin, year: "25 Years", title: "Las Vegas", description: "Spent the past 25 years in Las Vegas. Nevada isn't just where I live — it's home. My family is here. My life is here." },
    { icon: Heart, year: "Family", title: "Proud Father", description: "Became a dad to my now six-year-old son. I want for him what every parent wants — opportunity, a good-paying job, the ability to afford a home." },
  ];

  const stats = [
    { number: "39", label: "Years in Nevada" },
    { number: "25", label: "Years in Las Vegas" },
    { number: "6", label: "Year Old Son" },
    { number: "100%", label: "For Nevada Families" },
  ];

  return (
    <div data-testid="about-page" className="bg-[#0B1A3B] pt-16 md:pt-20">
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A3B] via-[#0F2251] to-[#0B1A3B]" />
        <div className="absolute top-1/3 -right-32 w-96 h-96 bg-[#D4AF37]/[0.04] rounded-full blur-[120px]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#D4AF37]/20 to-transparent blur-xl" />
              <div className="relative bg-white/[0.03] border border-white/10 p-1">
                <img src={ABOUT_PHOTO} alt="James Cooper" data-testid="about-main-photo" className="w-full object-cover" />
              </div>
              <div className="absolute -bottom-2 -right-2 w-12 h-12 border-b-2 border-r-2 border-[#D4AF37]" />
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Meet Cooper</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.9] text-white mb-6">
                A Nevadan.<br />A Father.<br />
                <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">A Problem Solver.</span>
              </h1>
              <p className="text-lg text-white/50 leading-relaxed mb-8">
                I'm James Cooper, but everyone calls me Cooper. I've called Nevada home for over 35 years. I'm a father who wants to make sure every Nevada child will be able to afford housing, find good jobs, and build a new life in the state we love.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="about-contact-btn" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.3)]">
                  Get in Touch
                </Link>
                <Link to="/volunteer" data-testid="about-volunteer-btn" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest text-sm transition-all">
                  Join the Team
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative py-12" data-testid="stats-section">
        <div className="absolute inset-0 bg-[#D4AF37]/10 border-y border-[#D4AF37]/20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={stat.label} className="text-center">
                <p className="text-4xl md:text-5xl font-black bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">{stat.number}</p>
                <p className="text-xs font-bold uppercase tracking-widest text-white/40 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 relative" data-testid="timeline-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A3B] to-[#0F2251]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">My Journey</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">A Nevada Story</h2>
          </div>

          <div className="relative">
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-white/10 transform md:-translate-x-1/2" />
            {timeline.map((item, i) => (
              <div key={item.title} data-testid={`timeline-item-${i}`} className={`relative flex items-start gap-8 mb-12 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                <div className="absolute left-0 md:left-1/2 w-16 h-16 flex items-center justify-center bg-[#0F2251] border-2 border-[#D4AF37]/40 transform md:-translate-x-1/2 z-10">
                  <item.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div className={`ml-24 md:ml-0 md:w-[calc(50%-4rem)] ${i % 2 === 0 ? "md:pr-8 md:text-right" : "md:pl-8"}`}>
                  <span className="text-xs font-bold text-[#D4AF37] uppercase tracking-widest">{item.year}</span>
                  <h3 className="text-lg font-bold text-white mt-1">{item.title}</h3>
                  <p className="text-white/50 text-sm mt-2 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-28 relative" data-testid="story-section">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">My Story</p>
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-8">Why This Matters</h2>
              <div className="space-y-5 text-white/50 leading-relaxed">
                <p>My family moved to Laughlin when I was just two years old, settling into a small apartment. My mom worked as a food server at the Edgewater, and my dad was a cook at the Golden Nugget while also taking construction jobs to make ends meet.</p>
                <p>Growing up, we didn't have much, but my parents never let that define us. They worked tirelessly to provide for my brother and me, showing us the value of hard work, sacrifice, and doing whatever it takes for family.</p>
                <p>I'm 41 years old and have lived in Nevada for 39 of those years. I've spent the past 25 years in Las Vegas, and I also lived in Reno while attending UNR. Nevada isn't just where I live, it's home.</p>
                <p>Today, I'm a father to a six-year-old son, and I want for him what every parent wants — opportunity, a good-paying job, the ability to afford a home.</p>
                <p className="text-white font-medium">That's why I'm running for Governor. Not for politics, but for people. To build a Nevada where hard work pays off and where every child has the opportunity to succeed.</p>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="sticky top-32">
                <div className="bg-white/[0.03] border border-white/10 p-6 mb-6">
                  <img src={EXTRA_PHOTO} alt="Cooper" data-testid="about-sidebar-photo" className="w-full object-cover mb-6" />
                  <h3 className="text-lg font-bold text-white mb-4">At a Glance</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3"><MapPin className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /><span className="text-white/50">39 years Nevada resident</span></li>
                    <li className="flex items-start gap-3"><Heart className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /><span className="text-white/50">Proud father of a 6-year-old</span></li>
                    <li className="flex items-start gap-3"><Users className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" /><span className="text-white/50">Community-focused leader</span></li>
                  </ul>
                </div>
                <Link to="/volunteer" data-testid="about-sidebar-volunteer" className="block w-full px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black text-center font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.3)]">
                  Get Involved
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
